<script lang="ts">
import {defineComponent} from 'vue';
import {SvgIcon} from '../svg.ts';
import $ from 'jquery';
import {BACKGROUND_LAYER, useLayersStore} from '../allspice_app/stores/layers.ts';

import {mapStores} from 'pinia';
import {useAllSpiceAppStore} from '../allspice_app/store.ts';
import {saveDiffFilterState} from '../allspice_app/diffFilterPersistence.ts';
import type {InspectorDataLoadEventDetails} from './allspice_render/types.ts';

type PcbLayerToggleData = {
  selectedPreset: string | null;
  presets: {
    name: string;
    layers: number[]
  }[];
  svg: SVGSVGElement | null;
  previousDetailZIndex: string;
  previousHeaderZIndex: string;
  isLoadingLayers: boolean;
};

export default defineComponent({
  name: 'PcbLayerToggle',
  components: {SvgIcon},
  props: {
    // True if displaying a diff between two commits, false if viewing a file.
    isDiff: {
      type: Boolean,
      required: true,
    },
    // Extra class names to apply to the button.
    buttonClassNames: {
      type: String,
      required: false,
      default: '',
    },
    anchor: {
      type: String,
      required: false,
      default: '',
    },
  },

  data(): PcbLayerToggleData {
    return {
      selectedPreset: null,
      presets: [],
      svg: null,
      previousDetailZIndex: '',
      previousHeaderZIndex: '',
      isLoadingLayers: true,
    };
  },

  computed: {
    ...mapStores(useLayersStore),
    hasLayers() {
      return this.layersStore.hasLayers;
    },
    hasLayerOverrides() {
      if (!this.layersStore.hasLayers) return false;
      return this.layersStore.layers.some((layer) => layer.isVisible !== layer.defaultIsVisible);
    },
  },

  mounted() {
    const $box = this.$el.closest(this.isDiff ? '.diff-file-box' : '.non-diff-file-content');
    if ($box) {
      $box.addEventListener('previewload', this.onPreviewLoad.bind(this));
      $box.addEventListener('inspectordataload', this.onInspectorDataLoad.bind(this));
    }

    $(this.$refs.dropdown as HTMLElement).dropdown({
      useLabels: false,
      onShow: this.onDropdownShow.bind(this),
      onHide: this.onDropdownHide.bind(this),
    });

    document.addEventListener('allspice-snippet-click-thru', this.applyQueryParams);

    // Sync SVG layer visibility whenever the store changes externally (e.g.
    // the Reset button in App.vue calls layersStore.resetToDefaults()).
    // PcbLayerToggle is mounted as a separate Vue app and is NOT remounted
    // when previewResetKey changes, so without this watcher the SVG DOM
    // would stay stale after a reset.
    this.$watch(
      () => this.layersStore.visibleLayerIds,
      () => this.reifySvgLayerVisibility(),
    );
  },

  unmounted() {
    document.removeEventListener('allspice-snippet-click-thru', this.applyQueryParams);
  },

  methods: {
    onPreviewLoad(event: CustomEvent) {
      event.preventDefault();
      this.svg = event.target as SVGSVGElement;
    },

    /**
     * @param {CustomEvent} event
     */
    onInspectorDataLoad(event: CustomEvent<InspectorDataLoadEventDetails>) {
      // add layers to dropdown based on order and set custom names
      this.layersStore.$reset();

      const store = useAllSpiceAppStore();
      const layerOverrides = store?.diffFilterConfig?.layerOverrides?.value ?? {};

      const newLayers = Object.values(event.detail.layers).map((layer) => ({
        id: layer.id,
        name: layer.name,
        isVisible: layerOverrides[layer.id] ?? layer.is_visible ?? true,
        defaultIsVisible: layer.is_visible ?? true,
      }));
      this.layersStore.layers = newLayers;

      this.presets = this.sortPresets(event.detail.presets);

      this.applyQueryParams();

      // Always sync SVG visibility with the layer store. applyQueryParams()
      // only calls reifySvgLayerVisibility() when URL has layer params.
      // Without this, file-default hidden layers won't be applied to a
      // freshly loaded SVG (e.g. after a reset-triggered remount).
      this.reifySvgLayerVisibility();

      this.recheckPresets();

      this.isLoadingLayers = false;
    },

    applyQueryParams() {
      // check for layers in query params
      const queryParameters = new URLSearchParams(window.location.search);
      // if this clickthrough has an anchor it must match the current diff file
      const anchorMatches = !queryParameters.has('anchor') || queryParameters.get('anchor') === this.anchor;
      const shouldApplyQueryParams = queryParameters.has('layers') && anchorMatches;

      if (!shouldApplyQueryParams) {
        return;
      }

      const showLayers = new Set((queryParameters.get('layers') ?? '').split(',').map(Number));

      this.layersStore.toggleAllLayers(false);
      for (const layerId of showLayers) {
        this.layersStore.toggleLayer(layerId, true);
      }
      this.reifySvgLayerVisibility();
    },

    // Called when the dropdown is shown.  Returning false would prevent the
    // default.
    onDropdownShow() {
      // If this is the file view, nothing to do.
      if (!this.isDiff) return;
      // The header has position: sticky, which creates its own stacking
      // context.  Increasing the z-index of the dropdown would have no effect.
      // To allow the dropdown to appear on top of the header of the next file
      // lower down in the page, we need to increase the z-index of this
      // dropdown's header.
      //
      // To prevent the header from appearing on top of the detail box where the
      // whitespace options are, we need to also increase the z-index of the
      // detail box.
      const detailBox: HTMLElement = document.querySelector('.diff-detail-box');
      const $header: HTMLElement = this.$el.closest('.diff-file-header');
      this.previousDetailZIndex = detailBox.style.zIndex;
      this.previousHeaderZIndex = $header.style.zIndex;
      detailBox.style.zIndex = '18';
      $header.style.zIndex = '17';
    },

    // Called when the dropdown is hidden.  Returning false would prevent the
    // default.
    onDropdownHide() {
      // If this is the file view, nothing to do.
      if (!this.isDiff) return;
      // Restore the z-index of the dropdown's header.
      const $header: HTMLElement = this.$el.closest('.diff-file-header');
      const detailBox: HTMLElement = document.querySelector('.diff-detail-box');
      // Restore in reverse order.
      $header.style.zIndex = this.previousHeaderZIndex;
      detailBox.style.zIndex = this.previousDetailZIndex;
      this.previousHeaderZIndex = '';
      this.previousDetailZIndex = '';
    },

    recheckPresets() {
      const selectedLayers = new Set(this.layersStore.visibleLayers.map((layer) => layer.id));
      for (const preset of this.presets) {
        if (preset.layers.length !== selectedLayers.size) {
          continue;
        }

        const presetLayers = new Set(preset.layers);
        if (presetLayers.symmetricDifference(selectedLayers).size === 0) {
          this.selectedPreset = preset.name;
          return;
        }
      }
      this.selectedPreset = null;
    },

    /**
     * @param {Layer} layer
     */
    toggleLayer(event: Event, layerId: number) {
      // Don't allow the other JS library to toggle active state of the menu item.
      event.stopPropagation();

      /** @type {boolean} */
      const newValue = this.layersStore.toggleLayer(layerId);
      this.setLayerVisibility(layerId, newValue);
      this.recheckPresets();
      this.persistLayerOverrides();
    },

    togglePreset(event: Event, preset: {name: string; layers: number[]}) {
      // Don't allow the other JS library to toggle active state of the menu
      // item.
      event.stopPropagation();

      this.selectedPreset = preset.name;
      this.layersStore.showPreset(preset.layers);
      this.reifySvgLayerVisibility();
      this.persistLayerOverrides();
    },

    reifySvgLayerVisibility() {
      if (!this.svg) {
        return;
      }

      const layerEls = this.svg.querySelectorAll(`[data-layer-id]`);
      for (const layerEl of layerEls) {
        const layerId = Number(layerEl.getAttribute('data-layer-id'));

        // the background layer is always shown, so we never change it
        if (layerId === BACKGROUND_LAYER.id) {
          continue;
        }

        const visible = this.layersStore.visibleLayerIds.includes(layerId);
        layerEl.classList.add(visible ? 'layer-shown' : 'layer-hidden');
        layerEl.classList.remove(visible ? 'layer-hidden' : 'layer-shown');
      }
    },

    selectAllLayers(event: Event) {
      // Don't allow the other JS library to toggle active state of the menu
      // item.
      event.stopPropagation();

      this.layersStore.toggleAllLayers(true);
      this.reifySvgLayerVisibility();
      this.recheckPresets();
      this.persistLayerOverrides();
    },

    selectNoLayers(event: Event) {
      // Don't allow the other JS library to toggle active state of the menu
      // item.
      event.stopPropagation();

      this.layersStore.toggleAllLayers(false);
      this.reifySvgLayerVisibility();
      this.recheckPresets();
      this.persistLayerOverrides();
    },

    /**
     * @param {Layer} layer
     * @param {boolean} newValue
     */
    setLayerVisibility(layerId: number, newValue: boolean) {
      if (!this.svg) {
        return;
      }

      const layerElms = this.svg.querySelectorAll(`[data-layer-id="${layerId}"]`);
      for (const layerElm of layerElms) {
        if (newValue) {
          layerElm.classList.remove('layer-hidden');
          layerElm.classList.add('layer-shown');
        } else {
          layerElm.classList.add('layer-hidden');
          layerElm.classList.remove('layer-shown');
        }
      }
    },

    persistLayerOverrides() {
      const store = useAllSpiceAppStore();
      if (!store) return;
      const overrides: Record<number, boolean> = {};
      for (const layer of this.layersStore.layers) {
        if (layer.isVisible !== layer.defaultIsVisible) {
          overrides[layer.id] = layer.isVisible;
        }
      }
      store.diffFilterConfig.layerOverrides.value = overrides;
      saveDiffFilterState(store);
    },

    /**
     * Sort presets by name in lexical order.
     */
    sortPresets(presetLayers: Record<string, number[]>): {name: string, layers: number[]}[] {
      const sortedPresets = [];
      const sortedPresestNames = Object.keys(presetLayers).sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase(), 'en', {numeric: true}),
      );
      for (const name of sortedPresestNames) {
        const layers = presetLayers[name];
        sortedPresets.push({name, layers});
      }

      return sortedPresets;
    },
  },
});
</script>

<template>
  <div class="pcb-layer-toggle">
    <div
      ref="dropdown"
      class="ui dropdown scrolling mini basic button"
      :class="buttonClassNames"
    >
      <span class="dropdown-title">
        Layers
        <span
          v-if="hasLayerOverrides"
          class="tw:inline-block tw:w-2 tw:h-2 tw:rounded-full tw:bg-primary tw:ml-1"
          title="Custom layer settings are active"
        />
      </span>
      <svg-icon name="octicon-triangle-down" :size="14" class-name="dropdown icon"/>
      <div class="ui menu">
        <div class="item layer-toggle-item" @click="selectAllLayers($event)" :class="isLoadingLayers ? 'disabled' : ''">
          Select All
        </div>
        <div class="item layer-toggle-item" @click="selectNoLayers($event)" :class="isLoadingLayers ? 'disabled' : ''">
          Select None
        </div>
        <hr class="ui divider menu-item-divider">
        <div v-if="!hasLayers && !isLoadingLayers" class="item disabled">
          (No Layers)
        </div>
        <div v-if="isLoadingLayers" class="item">
          <span class="is-loading tw:h-8 tw:w-full"/>
        </div>
        <div v-if="presets.length > 0">
          <div class="item">Presets</div>
          <div class="item layer-toggle-item" v-for="preset in presets" :key="preset.name" @click="togglePreset($event, preset)">
            <input type="checkbox" :checked="preset.name == selectedPreset" :disabled="preset.name == selectedPreset">
            {{ preset.name }}
          </div>
          <hr class="ui divider menu-item-divider">
          <div class="item">Layers</div>
        </div>
        <div class="item layer-toggle-item" v-for="layer in layersStore.layers" :key="layer.id" :class="{'active': !layer.isVisible}" @click="toggleLayer($event, layer.id)">
          <input type="checkbox" :checked="layer.isVisible">
          {{ layer.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

.pcb-layer-dropdown .dropdown-title {
  padding-right: 10px;
}

.dropdown-loader {
  position: absolute !important;
  left: 50% !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
}

.menu-item-divider {
  /* Reduce vertical margin. */
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  /* Remove rounded corners on the sides. */
  border-left-width: 0 !important;
  border-right-width: 0 !important;
}
</style>
